"use client";

import * as React from "react";

import styles from "./BlockWizard.module.css";

export interface BlockWizardProposal {
  blockType: "kpi" | "chart" | "table" | "insight" | "text" | "embed";
  title: string;
  reasoning?: string;
  assumptions?: string[];
  /** A few human-readable lines summarising the proposed config. */
  highlights: Array<{ label: string; value: string }>;
  /** Optional: the alternatives the model considered. */
  alternativeTypes?: string[];
}

export interface BlockWizardProps {
  onClose: () => void;
  onGenerate: (prompt: string) => Promise<void> | void;
  onAccept: () => void;
  onRetry: () => void;
  prompt: string;
  onPromptChange: (next: string) => void;
  status: "idle" | "thinking" | "ready" | "error";
  errorMessage?: string;
  proposal?: BlockWizardProposal | null;
  /** Suggestion chips above the prompt. */
  suggestions?: string[];
}

const DEFAULT_SUGGESTIONS = [
  "Which recruiters need help improving reply rate?",
  "Which clients are showing the most interest this week?",
  "Show whether floats are turning into replies and meetings",
  "What should I include in a client update?",
  "Which data problems should we fix first?",
  "Create a weekly summary for my manager",
];

/**
 * BlockWizard
 *
 * Natural-language path for adding a block. Wraps a prompt textarea,
 * suggestion chips, and a proposal preview with reasoning + assumptions.
 * The parent owns network state — this component is purely controlled.
 */
export function BlockWizard({
  onGenerate,
  onAccept,
  onRetry,
  prompt,
  onPromptChange,
  status,
  errorMessage,
  proposal,
  suggestions = DEFAULT_SUGGESTIONS,
}: BlockWizardProps) {
  const submit = React.useCallback(() => {
    if (status === "thinking") return;
    const trimmed = prompt.trim();
    if (trimmed.length < 3) return;
    void onGenerate(trimmed);
  }, [onGenerate, prompt, status]);

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className={styles.root} role="region" aria-label="Describe a report section with AI">
      <header className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.title}>Ask a recruiter question</div>
          <div className={styles.subtitle}>
            Type what you need to understand in plain English. Floats will pick the data,
            section type, and time period for you.
          </div>
        </div>
      </header>

      <div className={styles.promptArea}>
        <div style={{ position: "relative" }}>
          <textarea
            className={styles.textarea}
            placeholder='e.g. "Which clients should I follow up with this week?"'
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={status === "thinking"}
          />
          {prompt.length > 0 && status !== "thinking" ? (
            <button
              type="button"
              onClick={() => onPromptChange("")}
              aria-label="Clear prompt"
              title="Clear prompt"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 22,
                height: 22,
                borderRadius: 999,
                border: "1px solid var(--card-border, rgba(17,24,39,0.12))",
                background: "rgba(17,24,39,0.04)",
                color: "var(--text-muted)",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
        <div className={styles.toolbar}>
          <span className={styles.hint}>⌘ + Enter to create</span>
          <button
            type="button"
            className={styles.cta}
            onClick={submit}
            disabled={status === "thinking" || prompt.trim().length < 3}
          >
            {status === "thinking" ? "Creating…" : "Create section"}
          </button>
        </div>
      </div>

      {suggestions.length > 0 && !proposal && status !== "thinking" ? (
        <div className={styles.suggestions}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className={styles.suggestion}
              onClick={() => onPromptChange(s)}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      {status === "thinking" ? (
        <div className={styles.thinking} aria-live="polite">
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Creating a report section for your question…</span>
          <div className={styles.thinkingShimmer} style={{ width: "62%" }} />
          <div className={styles.thinkingShimmer} style={{ width: "88%" }} />
          <div className={styles.thinkingShimmer} style={{ width: "44%" }} />
        </div>
      ) : null}

      {status === "error" ? (
        <div className={styles.errorBox} role="alert">
          {errorMessage ?? "Floats could not create that section. Try asking the question more simply."}
        </div>
      ) : null}

      {status === "ready" && proposal ? (
        <div className={styles.proposal} aria-live="polite">
          <div className={styles.proposalHeader}>
            <div className={styles.proposalTitle}>{proposal.title}</div>
            <span className={styles.typeChip}>{sectionTypeLabel(proposal.blockType)}</span>
          </div>
          {proposal.reasoning ? (
            <div className={styles.reasoning}>{proposal.reasoning}</div>
          ) : null}
          <div className={styles.fieldGrid}>
            {proposal.highlights.map((h) => (
              <React.Fragment key={h.label}>
                <div className={styles.fieldKey}>{h.label}</div>
                <div className={styles.fieldVal}>{h.value}</div>
              </React.Fragment>
            ))}
          </div>
          {proposal.assumptions && proposal.assumptions.length > 0 ? (
            <div className={styles.assumptions}>
              <strong style={{ color: "var(--text-primary)", fontSize: 12, fontWeight: 600 }}>
                Assumptions to verify
              </strong>
              {proposal.assumptions.map((a, i) => (
                <div key={i} className={styles.assumptionItem}>
                  <span>{a}</span>
                </div>
              ))}
            </div>
          ) : null}
          <div className={styles.divider} />
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={onRetry}>
              Try a different angle
            </button>
            <button type="button" className={styles.cta} onClick={onAccept}>
              Add this section
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function sectionTypeLabel(type: BlockWizardProposal["blockType"]): string {
  return {
    kpi: "Number",
    chart: "Visual",
    table: "List",
    insight: "Insight",
    text: "Text",
    embed: "Record",
  }[type];
}
